import React, { useState, useEffect, useRef } from 'react';
import Container from '../components/common/Container';
import CustomQuestion from '../components/CustomQuestion';
import { resumeService, ResumeInfo, Question, QuestionCategory, AIResponse } from '../api/services/resume';

const HRBot: React.FC = () => {
  const [resume, setResume] = useState<ResumeInfo | null>(null);
  const [responses, setResponses] = useState<AIResponse[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [generatingAnswers, setGeneratingAnswers] = useState(false);
  const [expandedResponses, setExpandedResponses] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [questionCategories, setQuestionCategories] = useState<QuestionCategory | null>(null);
  const [filteredResponses, setFilteredResponses] = useState<AIResponse[]>([]);
  const [generatingCustomAnswer, setGeneratingCustomAnswer] = useState(false);
  const [customQuestionExpanded, setCustomQuestionExpanded] = useState(false);
  const responseRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Load latest resume and responses on component mount
  useEffect(() => {
    loadLatestResume();
  }, []);

  // Filter responses when category or responses change
  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredResponses(responses);
    } else {
      setFilteredResponses(responses.filter(response => response.question_category === selectedCategory));
    }
  }, [selectedCategory, responses]);

  const loadLatestResume = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await resumeService.getLatestResume();
      if (response.data) {
        setResume(response.data);
        // Load responses for this resume
        await loadResumeResponses(response.data.id);
        // Load question categories
        await loadQuestionCategories(response.data.id);
      } else {
        // No resume found, show upload form
        setShowUploadForm(true);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No resume found, show upload form
        setShowUploadForm(true);
      } else {
        setError(err.response?.data?.error || 'Failed to load resume.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadResumeResponses = async (resumeId: number) => {
    try {
      const response = await resumeService.getResumeResponses(resumeId);
      setResponses(response.data);
      // Collapse all responses by default
      setExpandedResponses(new Set());
    } catch (err: any) {
      // If no responses exist, that's okay - user can generate them
      console.log('No responses found for resume');
    }
  };

  const loadQuestionCategories = async (resumeId: number) => {
    try {
      const response = await resumeService.getQuestionCategories(resumeId);
      setQuestionCategories(response.data);
    } catch (err: any) {
      console.log('Failed to load question categories');
    }
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const response = await resumeService.uploadResume(file);
      if (response.data) {
        setResume(response.data);
        setResponses([]); // Clear old responses
        setExpandedResponses(new Set()); // Clear expanded state
        setShowUploadForm(false);
        // Load question categories first
        await loadQuestionCategories(response.data.id);
        // Generate answers for the new resume
        await generateAnswersForResume(response.data.id);
      } else {
        setError('Failed to process resume.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  // Generate answers for a resume
  const generateAnswersForResume = async (resumeId: number) => {
    setGeneratingAnswers(true);
    setError(null);

    try {
      const response = await resumeService.generateAnswers(resumeId);
      setResponses(response.data);
      // Collapse all responses by default when newly generated
      setExpandedResponses(new Set());

      // Start polling for updates
      pollForResponseUpdates(resumeId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate answers.');
    } finally {
      setGeneratingAnswers(false);
    }
  };

  // Poll for response updates
  const pollForResponseUpdates = async (resumeId: number) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await resumeService.getResumeResponses(resumeId);
        setResponses(response.data);

        // Check if all responses are completed or failed
        const allFinished = response.data.every((r: AIResponse) =>
          !r.status || r.status === 'completed' || r.status === 'failed'
        );

        if (allFinished) {
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.log('Error polling for updates:', err);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
    }, 300000);
  };

  // Regenerate answers for current resume
  const regenerateAnswers = async () => {
    if (!resume) return;
    await generateAnswersForResume(resume.id);
  };

  // Toggle response expansion
  const toggleResponse = (questionId: string) => {
    const newExpanded = new Set(expandedResponses);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedResponses(newExpanded);
  };

  // Handle custom question submission
  const handleCustomQuestion = async (question: string) => {
    if (!resume) return;

    setGeneratingCustomAnswer(true);
    setError(null);

    try {
      const response = await resumeService.askCustomQuestion(resume.id, question);
      // Add the new response to the list
      setResponses(prev => [...prev, response.data]);
      // Expand the new response
      setExpandedResponses(prev => new Set([...Array.from(prev), response.data.question_id]));

      // Start polling for updates
      pollForCustomResponse(response.data.question_id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit custom question.');
    } finally {
      setGeneratingCustomAnswer(false);
    }
  };

  // Poll for custom response updates
  const pollForCustomResponse = async (questionId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await resumeService.getResumeResponses(resume!.id);
        setResponses(response.data);

        // Check if the custom response is completed or failed
        const customResponse = response.data.find((r: AIResponse) => r.question_id === questionId);
        if (customResponse && (!customResponse.status || customResponse.status === 'completed' || customResponse.status === 'failed')) {
          clearInterval(pollInterval);
          // Scroll to the completed custom response
          setTimeout(() => scrollToResponse(questionId), 100);
        }
      } catch (err) {
        console.log('Error polling for custom response updates:', err);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 2 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
    }, 120000);
  };

  // Scroll to a specific response
  const scrollToResponse = (questionId: string) => {
    const element = responseRefs.current.get(questionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="py-4 sm:py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Your personalized interview preparation assistant
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm sm:text-base">{error}</p>
            </div>
          )}

          {/* Show latest resume and responses */}
          {resume && !showUploadForm && (
            <div className="space-y-4 sm:space-y-6">
              {/* Simplified Resume Summary - Now First */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      Resume Summary
                    </h2>
                    {resume.name && (
                      <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base mt-1">
                        {resume.name}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowUploadForm(true)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 text-sm sm:text-base"
                  >
                    Upload New Resume
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {resume.skills && resume.skills.length > 0 && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        {resume.skills.length}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Skills</div>
                    </div>
                  )}
                  {resume.experience && resume.experience.length > 0 && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        {resume.experience.length}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Experience</div>
                    </div>
                  )}
                  {resume.education && resume.education.length > 0 && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        {resume.education.length}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Education</div>
                    </div>
                  )}
                  {resume.projects && resume.projects.length > 0 && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        {resume.projects.length}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Projects</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Interview Responses */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      Interview Responses
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
                      AI-generated personalized answers based on your resume
                    </p>
                  </div>
                  <button
                    onClick={regenerateAnswers}
                    disabled={generatingAnswers}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 font-medium shadow-sm text-sm sm:text-base w-full sm:w-auto"
                  >
                    {generatingAnswers ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Regenerate Answers
                      </>
                    )}
                  </button>
                </div>

                {/* Filter Buttons */}
                {questionCategories && resume && !showUploadForm && (
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedCategory('all')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          selectedCategory === 'all'
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        All ({responses.length || 0})
                      </button>
                      {questionCategories.categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 capitalize ${
                            selectedCategory === category
                              ? 'bg-indigo-600 text-white shadow-md'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {category} ({questionCategories.counts[category]})
                        </button>
                      ))}
                      <button
                        onClick={() => setSelectedCategory('custom')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          selectedCategory === 'custom'
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        Custom ({responses.filter(r => r.question_category === 'custom').length})
                      </button>
                    </div>
                  </div>
                )}

                {/* Custom Question Section */}
                <div className="mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-700 overflow-hidden">
                    {/* Header - Always Visible */}
                    <div
                      className="p-4 sm:p-6 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800/30 transition-colors duration-200"
                      onClick={() => setCustomQuestionExpanded(!customQuestionExpanded)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg shadow-md mr-3 sm:mr-4">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                              Ask a Custom Question
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
                              Get personalized answers for any interview question
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <svg
                            className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                              customQuestionExpanded ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Content - Collapsible */}
                    {customQuestionExpanded && (
                      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                        <CustomQuestion
                          onSubmit={handleCustomQuestion}
                          isGenerating={generatingCustomAnswer}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {filteredResponses.length > 0 ? (
                  <div className="space-y-4 sm:space-y-6">
                    {filteredResponses.map((response, index) => (
                      <div
                        key={response.question_id}
                        ref={(el) => {
                          if (el) {
                            responseRefs.current.set(response.question_id, el);
                          }
                        }}
                        className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm overflow-hidden"
                      >
                        {/* Question Header - Always Visible */}
                        <div
                          className="p-4 sm:p-6 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
                          onClick={() => toggleResponse(response.question_id)}
                        >
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mr-3 sm:mr-4">
                              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg shadow-md ${
                                response.question_category === 'custom'
                                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600'
                                  : 'bg-gradient-to-r from-indigo-500 to-purple-600'
                              }`}>
                                {response.question_category === 'custom' ? (
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                ) : (
                                  index + 1
                                )}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3 leading-relaxed">
                                    {response.question_text}
                                  </h3>
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                                    <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 w-fit capitalize">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                      </svg>
                                      {response.question_category}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      AI Generated Response
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-shrink-0 ml-3">
                                  <svg
                                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                                      expandedResponses.has(response.question_id) ? 'rotate-180' : ''
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Answer - Collapsible */}
                        {expandedResponses.has(response.question_id) && (
                          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                            {(!response.status || response.status === 'completed') ? (
                              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 sm:p-5 border-l-4 border-green-500 shadow-sm">
                                <div className="flex items-start">
                                  <div className="flex-shrink-0 mr-2 sm:mr-3 mt-1">
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                                      <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm sm:text-base">
                                      {response.ai_response}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ) : response.status === 'generating' ? (
                              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 sm:p-5 border-l-4 border-yellow-500 shadow-sm">
                                <div className="flex items-center">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600 mr-3"></div>
                                  <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                                    Generating personalized response...
                                  </p>
                                </div>
                              </div>
                            ) : response.status === 'failed' ? (
                              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 sm:p-5 border-l-4 border-red-500 shadow-sm">
                                <div className="flex items-start">
                                  <div className="flex-shrink-0 mr-2 sm:mr-3 mt-1">
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                                      <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-red-700 dark:text-red-300 text-sm sm:text-base">
                                      {response.ai_response}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 sm:p-5 border-l-4 border-gray-500 shadow-sm">
                                <div className="flex items-center">
                                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-100 dark:bg-gray-900 rounded-full flex items-center justify-center mr-3">
                                    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                                    Waiting to start generation...
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : responses.length > 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">
                      No responses in this category
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      Try selecting a different category or generate answers for all questions.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No Interview Responses Yet
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto text-sm sm:text-base px-4">
                      Generate personalized interview answers based on your resume to help you prepare for your next interview.
                    </p>
                    <button
                      onClick={regenerateAnswers}
                      disabled={generatingAnswers}
                      className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 sm:px-8 py-3 rounded-lg hover:from-indigo-600 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 font-medium shadow-lg text-sm sm:text-base w-full sm:w-auto max-w-xs"
                    >
                      {generatingAnswers ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline"></div>
                          Generating Answers...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Generate Interview Answers
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upload Form */}
          {showUploadForm && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  Upload New Resume
                </h2>
                {resume && (
                  <button
                    onClick={() => setShowUploadForm(false)}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm sm:text-base w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                )}
              </div>

              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 sm:p-8 text-center">
                <input
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="resume-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="resume-upload"
                  className={`cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md ${
                    uploading
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    'Choose File'
                  )}
                </label>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Supported formats: PDF, DOCX, TXT
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default HRBot;